import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { useEffect, useMemo, useState } from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getLegislationById } from "../../../services";
import { getQueryValue } from "../../../store/selectors";
import {
  getHighlightedExcerpts,
  handleClickNote,
  parseChapter,
  printLegislationContent,
  processDocumentText,
} from "../../../utils";
import { Pagewrapper } from "../../components";
import { ActionButton, Toggle } from "../../components/global";
import LexLoading from "../../components/global/_lexLoading";
import NotesModal from "../../components/global/_notesModal";
import "./index.scss";

const LegislationDetails = (props) => {
  const { id } = useParams();

  const [legislation, setLegislation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hideRevoked, setHideRevoked] = useState(false);

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [noteTitle, setNoteTitle] = useState("");
  const [modalType, setModalType] = useState("note");
  const [articleId, setArticleId] = useState("");
  const [articleUrl, setArticleUrl] = useState("");

  const query = useSelector(getQueryValue);

  const handlePrint = () => {
    console.log("Click para imprimir");
    printLegislationContent({
      legislation,
      hideRevoked,
    });
  };

  const processedText = useMemo(() => {
    if (!legislation?.texto) return "";

    const nicknames =
      legislation?.apelidos?.filter((name) => name !== legislation.nome) || [];

    return processDocumentText({
      htmlText: `${
        nicknames ? `<p class="nicknames"> ${nicknames.join(" - ")} </p>` : ""
      } ${legislation.texto}`,
      hideRevoked,
    });
  }, [
    legislation?.texto,
    legislation?.apelidos,
    legislation?.nome,
    hideRevoked,
  ]);

  useEffect(() => {
    const loadLegislation = async () => {
      setShowNoteModal(false);
      try {
        setLoading(true);
        setError(null);

        const response = await getLegislationById(id);

        setLegislation(response);
      } catch (err) {
        setError(err.response.data.message || "Erro ao carregar a modelo");
        console.error("Erro:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadLegislation();
    }
  }, [id]);

  useEffect(() => {
    const handleGlobalClick = (event) => {
      const target = event.target;
      if (showNoteModal) return;

      if (target.classList?.contains("nota-super")) {
        handleClickNote(event);
        return;
      }

      if (target.hasAttribute("data-nota-link")) {
        event.preventDefault();

        const noteKey = target.getAttribute("data-nota-link");
        if (!noteKey) return;

        const note = legislation?.notas?.[noteKey];
        if (!note) return;

        let titleClicked = "";

        const tag = target.tagName.toLowerCase();

        if (
          tag === "a" ||
          tag === "strong" ||
          tag === "p" ||
          /^h[1-6]$/.test(tag)
        ) {
          titleClicked = target.textContent?.trim();
        }

        if (!titleClicked) {
          titleClicked = note?.descricao || `Nota ${noteKey}`;
        }

        const texto = note?.["texto:"] || note?.texto || "";
        setModalType("note");
        setNoteTitle(titleClicked);
        setNoteContent(processDocumentText({ htmlText: texto, hideRevoked }));
        setShowNoteModal(true);
      }

      if (target.tagName?.toLowerCase() === "a") {
        const href = target.getAttribute("href") || "";

        const match = href.match(/(art\d+)/i);

        if (match) {
          event.preventDefault();

          const articlePart = match[1];

          let cleaned = href.replace(/^\/?legislacao\//i, "");
          cleaned = cleaned.split("#")[0];

          if (!cleaned.toLowerCase().endsWith(articlePart.toLowerCase())) {
            cleaned = cleaned.replace(/art\d+/i, "") + articlePart;
          }

          setArticleId(cleaned);
          setArticleUrl(href);
          setModalType("legislation");
          setShowNoteModal(true);

          return;
        }
      }
    };

    document.addEventListener("click", handleGlobalClick);
    return () => document.removeEventListener("click", handleGlobalClick);
  }, [legislation, showNoteModal]);

  useEffect(() => {
    if (loading) return;
    if (!legislation) return;

    const hash = window.location.hash;
    if (!hash) return;

    const id = hash.replace("#", "");

    const timer = setTimeout(() => {
      const target = document.getElementById(id);

      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        let retries = 0;
        const retryInterval = setInterval(() => {
          const el = document.getElementById(id);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            clearInterval(retryInterval);
          }
          retries++;
          if (retries > 20) {
            clearInterval(retryInterval);
          }
        }, 200);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [loading, legislation]);

  if (loading) {
    return (
      <Pagewrapper {...props}>
        <Container>
          <Row>
            <Col>
              <div className="content-loading">
                <LexLoading customText="Buscando legislação..." />
              </div>
            </Col>
          </Row>
        </Container>
      </Pagewrapper>
    );
  }

  if (error) {
    return (
      <Pagewrapper {...props}>
        <Container>
          <Row>
            <Col>
              <div className="error-section text-center">
                <h4 className="text-danger">
                  Ops! Parece que ocorreu um erro.
                </h4>
                <p>{error}</p>
                <small className="text-muted">Legislação: {id}</small>
              </div>
            </Col>
          </Row>
        </Container>
      </Pagewrapper>
    );
  }

  return (
    <Pagewrapper {...props}>
      <Container>
        <Row>
          <Col>
            <div id="details-content" className="mt-4">
              <div className="content-header">
                <Toggle
                  label="Texto compilado"
                  checked={hideRevoked}
                  onChange={() => {
                    setHideRevoked((prev) => !prev);
                  }}
                />
                <div className="content-actions d-flex justify-content-end gap-2">
                  <ActionButton
                    icon={<BookmarkBorderOutlinedIcon fontSize="small" />}
                    onClick={() => {
                      try {
                        if (window.external && window.external.AddFavorite) {
                          window.external.AddFavorite(
                            window.location.href,
                            document.title
                          );
                        } else if (window.sidebar && window.sidebar.addPanel) {
                          window.sidebar.addPanel(
                            document.title,
                            window.location.href,
                            ""
                          );
                        } else {
                          const isMac =
                            navigator.platform.toUpperCase().indexOf("MAC") >=
                            0;
                          const shortcut = isMac ? "Cmd+D" : "Ctrl+D";
                          alert(
                            `Para adicionar aos favoritos, pressione ${shortcut} ou use o menu do seu navegador.`
                          );
                        }
                      } catch (error) {
                        console.error(
                          "Erro ao tentar adicionar aos favoritos:",
                          error
                        );
                        const isMac =
                          navigator.platform.toUpperCase().indexOf("MAC") >= 0;
                        const shortcut = isMac ? "Cmd+D" : "Ctrl+D";
                        alert(
                          `Para adicionar aos favoritos, pressione ${shortcut} ou use o menu do seu navegador.`
                        );
                      }
                    }}
                    label="Salvar"
                  />

                  <ActionButton
                    icon={<PrintOutlinedIcon fontSize="small" />}
                    label="Imprimir"
                    onClick={handlePrint}
                  />
                </div>
              </div>

              <div className="legislation content-box">
                {legislation?.texto ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: !!query
                        ? getHighlightedExcerpts(
                            processedText,
                            query,
                            processedText.length,
                            false
                          )
                        : processedText,
                    }}
                    className="content-text"
                  />
                ) : (
                  <p className="text-muted">Conteúdo não disponível.</p>
                )}
              </div>

              <div className="content-box">
                {legislation?.artigos?.length &&
                  legislation.artigos.map((art, index) => {
                    const processed = processDocumentText({
                      htmlText: art.texto,
                      hideRevoked,
                    });

                    const highlighted = query
                      ? getHighlightedExcerpts(
                          processed,
                          query,
                          processed.length,
                          false
                        )
                      : processed;

                    const prev = legislation.artigos[index - 1];

                    const showBook = !prev || prev.livro !== art.livro;
                    const showTitle = !prev || prev.titulo !== art.titulo;
                    const showChapter = !prev || prev.capitulo !== art.capitulo;
                    const showSecao = !prev || prev.secao !== art.secao;

                    return (
                      <div key={index} className="article-item">
                        {showBook && art.livro && (
                          <div className="article-title-box">
                            <p
                              className="article-chapter"
                              dangerouslySetInnerHTML={{
                                __html: art.livro.replace(" - ", "<br/>"),
                              }}
                            />
                          </div>
                        )}

                        {showTitle && art.titulo && (
                          <div className="article-title-box">
                            <p
                              className="article-chapter"
                              dangerouslySetInnerHTML={{
                                __html: art.titulo.replace(" - ", "<br/>"),
                              }}
                            />
                          </div>
                        )}

                        {showChapter &&
                          art.capitulo &&
                          (() => {
                            const { title, notes } = parseChapter(
                              art.capitulo,
                              query
                            );

                            return (
                              <div className="article-title-box">
                                <p
                                  className="article-chapter"
                                  dangerouslySetInnerHTML={{
                                    __html: String(title)?.replace(
                                      " - ",
                                      "<br/>"
                                    ),
                                  }}
                                />
                                {notes && (
                                  <div
                                    className="content-text"
                                    dangerouslySetInnerHTML={{ __html: notes }}
                                  />
                                )}
                              </div>
                            );
                          })()}

                        {showSecao && art.secao && (
                          <div className="article-title-box">
                            <p
                              className="article-chapter"
                              dangerouslySetInnerHTML={{
                                __html: art.secao.replace(" - ", "<br/>"),
                              }}
                            />
                          </div>
                        )}

                        <div
                          className="content-text"
                          dangerouslySetInnerHTML={{ __html: highlighted }}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>

            <NotesModal
              noteContent={noteContent}
              noteTitle={noteTitle}
              setShowNoteModal={setShowNoteModal}
              showNoteModal={showNoteModal}
              type={modalType}
              articleId={articleId}
              url={articleUrl}
            />
          </Col>
        </Row>
      </Container>
    </Pagewrapper>
  );
};

export default LegislationDetails;
